import { ApiQueryParams, DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { BudgetUseClient } from '@frontend/budget/api';
import { BudgetUse, ProductBudgetUse } from '@frontend/budget/types';
import { Application, Feature } from '@frontend/functionality/types';
import { PackageClient } from '@frontend/package/api';
import { PackageStateName, PackageType } from '@frontend/package/types';
import { IProduct } from '@frontend/product/types';
import { SettingKey } from '@frontend/setting/types';
import { SlotWorkflowClient } from '@frontend/slot/api';
import { TransactionActorClient, TransactionClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { TransactionActorType, TransactionStateName, TransactionTriggerName, TransactionType } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useWizardPackage } from '../../hooks/useWizardPackage';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

export interface BudgetUseWithProduct extends BudgetUse {
    product: IProduct;
}

interface ViewProps {
    accountId: string | null;
    isLoading: boolean;
    setIsLoading: (isLoading: boolean) => void;
    isProcessing: boolean;
    error: string | null;
    resetError: () => void;
    fetchBudgetUses: (queryParams?: ApiQueryParams<DefaultQueryParams>) => Promise<PaginatedResponse<ProductBudgetUse>>;
    selectedProduct: string | null;
    setSelectedProduct: (productId: string | null) => void;
    onRecycle: () => void;
    hasOtherRecycle: boolean;
    onOtherRecycle: () => void;
}

export const useRecycleOverview = (): ViewProps => {
    const { store, navigationState, onSubmit, updateWizardData } = useScreen();
    const intl = useIntl();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const otherRecycleStep = useMemo(
        () => store?.steps.find((step) => step.screen === ScreenType.RECYCLE_OVERVIEW && step.trigger === ScreenTrigger.SUBMIT_FALSE),
        [store?.steps]
    );

    const transactionContext = useWizardTransaction({
        store: store,
        wizardId: store?.wizard.id
    });
    const packageContext = useWizardPackage({
        store: store,
        wizardId: store?.wizard.id,
        autoFetch: false,
        autoCreate: false
    });

    const workflowInfo = useMemo(() => {
        const settings = store?.functionalitySettings;
        if (!settings) return null;
        const transactionWf = settings.find((s) => s.key === SettingKey.TRANSACTION_WORKFLOW_ID)?.value as string;
        const dropWf = settings.find((s) => s.key === SettingKey.DROP_OFF_PACKAGE_WORKFLOW_ID)?.value as string;
        const pickWf = settings.find((s) => s.key === SettingKey.PICK_UP_PACKAGE_WORKFLOW_ID)?.value as string | undefined;
        if (!transactionWf || !dropWf) return null;
        return { transactionWf, dropWf, pickWf };
    }, [store?.functionalitySettings]);

    const fetchBudgetUses = useCallback(
        async (queryParams?: ApiQueryParams<DefaultQueryParams>): Promise<PaginatedResponse<ProductBudgetUse>> => {
            if (!navigationState.user) return { results: [], count: 0 };

            const res = await BudgetUseClient.fetchProductBudgetUses(navigationState.user.accountId, navigationState.user.id, queryParams);

            return {
                results: res.results,
                count: res.count
            };
        },
        [navigationState.user]
    );

    const onRecycle = useCallback(async () => {
        if (!selectedProduct || !workflowInfo || !navigationState.iot || !store?.wizard || !store?.functionality) return;
        setIsProcessing(true);
        try {
            const accountId = navigationState.iot.account_id;
            const spotId = navigationState.iot.spot_id;
            if (!accountId || !spotId) return;
            const slotsResult = await SlotWorkflowClient.postAvailableSlots(accountId, spotId, {
                account_id: accountId,
                application: store.functionality.application,
                feature: Feature.RECYCLE_TRANSACTION,
                functionality_id: store.functionality.id,
                product_id: selectedProduct,
                transaction_type: TransactionType.VENDING
            });
            if (slotsResult.results.length === 0) {
                setError(
                    intl.formatMessage({
                        id: 'kiosk.wizard.recycle-overview.no-available-slots',
                        defaultMessage: 'No available slots found for this product.'
                    })
                );
                return;
            }
            const availableSlot = slotsResult.results[0];

            const transaction = await TransactionClient.postTransaction(accountId, {
                type: TransactionType.VENDING,
                workflow_id: workflowInfo.transactionWf,
                spot_id: spotId,
                module_id: availableSlot.module_id,
                slot_id: availableSlot.id,
                user_id: navigationState.user?.id,
                iot_id: navigationState.iot.id,
                application: store.functionality.application,
                feature: Feature.RECYCLE_TRANSACTION,
                state: TransactionStateName.TRANSACTION_CREATED
            });

            await transactionContext.localUpdateTransaction({
                id: transaction.id,
                state: transaction.state,
                workflow_id: transaction.workflow_id,
                type: transaction.type,
                user_id: transaction.user_id,
                spot_id: transaction.spot_id,
                module_id: transaction.module_id,
                slot_id: transaction.slot_id,
                iot_id: transaction.iot_id,
                application: transaction.application,
                feature: transaction.feature
            });

            if (navigationState.user?.id) {
                TransactionActorClient.postTransactionActor(transaction.account_id, transaction.id, {
                    type: TransactionActorType.DROPPED_OFF_BY,
                    user_id: navigationState.user.id
                });
            }

            const dropPkg = await PackageClient.postPackage(transaction.account_id, transaction.id, {
                state: PackageStateName.PACKAGE_CREATED,
                workflow_id: workflowInfo.dropWf,
                user_id: transaction.user_id,
                quantity: 1,
                type: PackageType.DROP_OFF,
                spot_id: availableSlot.spot_id,
                module_id: availableSlot.module_id,
                slot_id: availableSlot.id,
                product_id: selectedProduct,
                application: Application.VENDING,
                feature: Feature.RECYCLE_TRANSACTION
            });
            await packageContext.localUpdatePackage(dropPkg);

            if (workflowInfo.pickWf) {
                await PackageClient.postPackage(transaction.account_id, transaction.id, {
                    state: PackageStateName.PACKAGE_CREATED,
                    workflow_id: workflowInfo.pickWf,
                    user_id: transaction.user_id,
                    quantity: 1,
                    type: PackageType.AUDIT,
                    spot_id: availableSlot.spot_id,
                    module_id: availableSlot.module_id,
                    slot_id: availableSlot.id,
                    product_id: selectedProduct,
                    dependent_package_id: dropPkg.id,
                    application: Application.VENDING,
                    feature: Feature.RECYCLE_TRANSACTION
                });
            }

            await TransactionWorkflowClient.triggerTransactionState(transaction.account_id, transaction.id, {
                trigger: TransactionTriggerName.TRANSACTION_PROCESS,
                location: navigationState.spot?.id ?? undefined,
                actor: navigationState.user?.id ?? undefined
            });

            onSubmit();
        } finally {
            setIsProcessing(false);
        }
    }, [selectedProduct, workflowInfo, navigationState, store, transactionContext, packageContext, onSubmit, intl]);

    const onOtherRecycle = useCallback(() => {
        if (!otherRecycleStep) return;
        updateWizardData({
            browseProductsContext: {
                feature: Feature.RECYCLE_TRANSACTION,
                skipStockCheck: true,
                skipAllowanceCheck: true
            }
        });
        onSubmit(otherRecycleStep);
    }, [otherRecycleStep, onSubmit, updateWizardData]);

    return {
        accountId: navigationState.user?.accountId ?? null,
        isLoading,
        setIsLoading,
        isProcessing,
        error,
        resetError: useCallback(() => setError(null), []),
        fetchBudgetUses,
        selectedProduct,
        setSelectedProduct,
        onRecycle,
        hasOtherRecycle: !!otherRecycleStep,
        onOtherRecycle
    };
};

export default useRecycleOverview;
